import { useState, useEffect, useCallback } from 'react';
import { Calendar, Loader2, Check, FileDown, RefreshCw, Users, ArrowUp, ArrowDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { getMatches, getTeams, getGroups, getSettings, updateMatch, reassignCalendarDates, swapMatchSlots, resetMatchResult, recalculateTeamStats } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export const AdminPartitePage = () => {
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [editingMatch, setEditingMatch] = useState(null);
  const [scores, setScores] = useState({ home: 0, away: 0 });
  const [saving, setSaving] = useState(false);
  const [reassigning, setReassigning] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      const [matchesRes, teamsRes, groupsRes, settingsRes] = await Promise.all([
        getMatches(),
        getTeams(),
        getGroups(),
        getSettings()
      ]);
      setMatches(matchesRes.data);
      setTeams(teamsRes.data);
      setGroups(groupsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTeamById = (id) => teams.find(t => t.id === id);
  const getGroupById = (id) => groups.find(g => g.id === id);

  // Build label map from settings match_schedule
  const getLabelForDate = (dateStr) => {
    const schedule = settings?.match_schedule || [];
    const match = schedule.find(s => s.date === dateStr);
    return match?.label || '';
  };

  const filteredMatches = matches
    .filter(m => {
      if (m.phase !== 'group') return false;
      if (filter === 'played') return m.is_played;
      if (filter === 'upcoming') return !m.is_played;
      return true;
    })
    .sort((a, b) => new Date(a.match_date) - new Date(b.match_date));

  // Group matches by date
  const matchesByDate = {};
  filteredMatches.forEach(match => {
    const dateKey = match.match_date?.substring(0, 10) || 'unknown';
    if (!matchesByDate[dateKey]) matchesByDate[dateKey] = [];
    matchesByDate[dateKey].push(match);
  });

  const sortedDates = Object.keys(matchesByDate).sort();

  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00Z');
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatTime = (isoStr) => {
    const date = new Date(isoStr);
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
  };

  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setScores({ home: match.home_score || 0, away: match.away_score || 0 });
  };

  const handleSwapSlot = async (match, other) => {
    try {
      await swapMatchSlots(match.id, other.id);
      toast.success('Orari scambiati');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nello scambio');
    }
  };

  const handleReassignDates = async () => {
    setReassigning(true);
    try {
      const res = await reassignCalendarDates();
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento date');
    } finally {
      setReassigning(false);
    }
  };

  const handleRecalcStats = async () => {
    if (!window.confirm('Ricalcolare da zero le statistiche delle squadre in base alle partite giocate? Utile se i totali (DR, gol fatti/subiti) sembrano incoerenti.')) return;
    try {
      const res = await recalculateTeamStats();
      toast.success(res.data.message || 'Statistiche ricalcolate');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel ricalcolo statistiche');
    }
  };

  const exportToPDF = () => {
    const groupMatches = matches.filter(m => m.phase === 'group');
    const sortedMatches = [...groupMatches].sort((a, b) => new Date(a.match_date) - new Date(b.match_date));

    const pdfMatchesByDate = {};
    sortedMatches.forEach(match => {
      const dateKey = match.match_date?.substring(0, 10) || 'unknown';
      if (!pdfMatchesByDate[dateKey]) pdfMatchesByDate[dateKey] = [];
      pdfMatchesByDate[dateKey].push(match);
    });

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Calendario Torneo Notturno Viadana</title>
        <style>
          @page { size: A4 portrait; margin: 8mm; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; }
          body { font-family: Arial, Helvetica, sans-serif; padding: 4mm 6mm; color: #111; font-size: 9.5pt; }
          h1 { text-align: center; margin: 0 0 2mm; font-size: 16pt; }
          h2 { text-align: center; color: #666; margin: 0 0 4mm; font-weight: normal; font-size: 10pt; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 3px 6px; border: 1px solid #d0d0d0; line-height: 1.2; }
          th { background: #f0f0f0; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.3px; }
          .date-row td { background: #1a1a2e; color: #fff; font-weight: bold; padding: 4px 6px; font-size: 9pt; }
          .date-row .label { color: #39FF14; font-weight: normal; float: right; font-size: 8pt; }
          .group { font-weight: bold; color: #2a7a2a; text-align: center; width: 36px; }
          .vs { text-align: center; color: #888; width: 24px; }
          .score { text-align: center; font-weight: bold; width: 60px; }
          .time { text-align: center; color: #555; width: 50px; font-variant-numeric: tabular-nums; }
          .home, .away { width: auto; }
          .home { text-align: right; }
          .played { background: #f4fff4; }
          .footer { text-align: center; margin-top: 4mm; color: #999; font-size: 7.5pt; }
          @media print { body { padding: 4mm; } }
        </style>
      </head>
      <body>
        <h1>TORNEO NOTTURNO DI CALCIO</h1>
        <h2>30a Edizione - Viadana Bresciana - Calendario Gironi</h2>
        <table>
          <thead>
            <tr>
              <th class="time">Ora</th>
              <th class="group">Gir.</th>
              <th class="home">Squadra Casa</th>
              <th class="vs"></th>
              <th>Squadra Ospite</th>
              <th class="score">Risultato</th>
            </tr>
          </thead>
          <tbody>
            ${Object.keys(pdfMatchesByDate).sort().map(dateKey => {
              const dayMatches = pdfMatchesByDate[dateKey].sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
              const label = getLabelForDate(dateKey);
              const dateFormatted = new Date(dateKey + 'T12:00:00Z').toLocaleDateString('it-IT', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
              });
              return `
                <tr class="date-row"><td colspan="6">${dateFormatted}${label ? `<span class="label">${label}</span>` : ''}</td></tr>
                ${dayMatches.map(match => {
                  const homeTeam = getTeamById(match.home_team_id);
                  const awayTeam = getTeamById(match.away_team_id);
                  const group = getGroupById(match.group_id);
                  const time = new Date(match.match_date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
                  return `
                    <tr class="${match.is_played ? 'played' : ''}">
                      <td class="time">${time}</td>
                      <td class="group">${group?.name || '-'}</td>
                      <td class="home">${homeTeam?.name || 'TBD'}</td>
                      <td class="vs">vs</td>
                      <td>${awayTeam?.name || 'TBD'}</td>
                      <td class="score">${match.is_played ? `${match.home_score} - ${match.away_score}` : '-'}</td>
                    </tr>
                  `;
                }).join('')}
              `;
            }).join('')}
          </tbody>
        </table>
        <p class="footer">Generato il ${new Date().toLocaleDateString('it-IT')} - Torneo Notturno Viadana Bresciana</p>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const handleSaveResult = async () => {
    if (!editingMatch) return;

    setSaving(true);
    try {
      await updateMatch(editingMatch.id, {
        home_score: parseInt(scores.home),
        away_score: parseInt(scores.away),
        is_played: true
      });
      toast.success('Risultato salvato');
      setEditingMatch(null);
      fetchData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleResetResult = async () => {
    if (!editingMatch) return;
    if (!window.confirm('Annullare il risultato? Il punteggio e i marcatori verranno cancellati e le statistiche del girone saranno aggiornate.')) return;
    try {
      await resetMatchResult(editingMatch.id);
      toast.success('Risultato annullato');
      setEditingMatch(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante l\'annullamento');
    }
  };

  const handleDownloadReport = async (dateKey) => {    try {
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/report/giornata/${dateKey}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Errore nel download');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_giornata_${dateKey}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report scaricato');
    } catch (error) {
      toast.error('Errore nel download del report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalGroupMatches = matches.filter(m => m.phase === 'group').length;
  const playedGroupMatches = matches.filter(m => m.phase === 'group' && m.is_played).length;

  return (
    <div className="p-4 md:p-6" data-testid="admin-partite">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">GESTIONE PARTITE</h1>
            <p className="text-slate-400 text-sm">
              {playedGroupMatches}/{totalGroupMatches} partite giocate
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <Button
                onClick={handleReassignDates}
                disabled={reassigning}
                variant="outline"
                className="border-slate-700 hover:border-primary"
                data-testid="reassign-dates-partite-btn"
              >
                {reassigning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Aggiorna Date
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={handleRecalcStats}
                variant="outline"
                className="border-slate-700 hover:border-primary"
                data-testid="recalc-stats-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Ricalcola Classifiche
              </Button>
            )}
            <Button
              onClick={exportToPDF}
              variant="outline"
              className="border-slate-700 hover:border-primary"
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              Esporta PDF
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[200px] bg-slate-900 border-slate-800">
              <SelectValue placeholder="Filtra partite" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="upcoming">Da giocare</SelectItem>
              <SelectItem value="played">Giocate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Matches grouped by date */}
        {sortedDates.length > 0 ? (
          <div className="space-y-6">
            {sortedDates.map(dateKey => {
              const dayMatches = matchesByDate[dateKey].sort((a, b) => new Date(a.match_date) - new Date(b.match_date));
              const label = getLabelForDate(dateKey);
              const playedCount = dayMatches.filter(m => m.is_played).length;

              return (
                <div key={dateKey} data-testid={`match-day-${dateKey}`}>
                  {/* Day Header */}
                  <div className="flex items-center justify-between bg-slate-800/60 rounded-t-lg px-4 py-3 border border-slate-700">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span className="font-heading font-bold capitalize text-sm md:text-base">
                        {formatDateHeader(dateKey)}
                      </span>
                      {label && (
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">
                          {label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">
                        {dayMatches.length} partite {playedCount > 0 && `(${playedCount} giocate)`}
                      </span>
                      {playedCount > 0 && (
                        <button
                          onClick={() => handleDownloadReport(dateKey)}
                          className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-primary transition-colors"
                          title="Scarica Report PDF"
                          data-testid={`download-report-${dateKey}`}
                        >
                          <FileDown className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Day Matches */}
                  <div className="border border-t-0 border-slate-700 rounded-b-lg divide-y divide-slate-800">
                    {dayMatches.map((match, mIdx) => {
                      const homeTeam = getTeamById(match.home_team_id);
                      const awayTeam = getTeamById(match.away_team_id);
                      const group = getGroupById(match.group_id);
                      const prevMatch = mIdx > 0 ? dayMatches[mIdx - 1] : null;
                      const nextMatch = mIdx < dayMatches.length - 1 ? dayMatches[mIdx + 1] : null;

                      return (
                        <div
                          key={match.id}
                          className={cn(
                            "flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 md:p-4 min-w-0",
                            match.is_played && "bg-green-500/5"
                          )}
                        >
                          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0 flex-wrap">
                            {/* Time + reorder arrows */}
                            <div className="flex items-center gap-1 shrink-0">
                              {isAdmin && (
                                <div className="flex flex-col -gap-0.5">
                                  <button
                                    onClick={() => prevMatch && handleSwapSlot(match, prevMatch)}
                                    disabled={!prevMatch}
                                    className="p-0.5 rounded hover:bg-slate-700 text-slate-500 hover:text-primary disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                    title="Sposta in alto (scambia orario)"
                                    data-testid={`swap-up-${match.id}`}
                                  >
                                    <ArrowUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => nextMatch && handleSwapSlot(match, nextMatch)}
                                    disabled={!nextMatch}
                                    className="p-0.5 rounded hover:bg-slate-700 text-slate-500 hover:text-primary disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                                    title="Sposta in basso (scambia orario)"
                                    data-testid={`swap-down-${match.id}`}
                                  >
                                    <ArrowDown className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                              <span className="text-sm font-numbers text-slate-400 w-12">
                                {formatTime(match.match_date)}
                              </span>
                            </div>

                            {/* Group badge */}
                            {group && (
                              <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded shrink-0">
                                {group.name}
                              </span>
                            )}

                            {/* Teams */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-1 justify-end min-w-0">
                                <span className="font-heading font-bold text-sm truncate">
                                  {homeTeam?.name || 'TBD'}
                                </span>
                                {homeTeam && (
                                  <button
                                    onClick={() => navigate(`/admin/giocatori?team=${homeTeam.id}`)}
                                    className="p-1 rounded hover:bg-slate-700/60 text-slate-500 hover:text-primary transition-colors shrink-0"
                                    title={`Giocatori ${homeTeam.name}`}
                                    data-testid={`players-link-${homeTeam.id}`}
                                  >
                                    <Users className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>

                              {match.is_played ? (
                                <div className="flex items-center gap-1.5 px-2 shrink-0">
                                  <span className="font-numbers text-lg font-bold text-primary">{match.home_score}</span>
                                  <span className="text-slate-600">-</span>
                                  <span className="font-numbers text-lg font-bold text-primary">{match.away_score}</span>
                                </div>
                              ) : (
                                <span className="px-2 text-slate-500 shrink-0">vs</span>
                              )}

                              <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                {awayTeam && (
                                  <button
                                    onClick={() => navigate(`/admin/giocatori?team=${awayTeam.id}`)}
                                    className="p-1 rounded hover:bg-slate-700/60 text-slate-500 hover:text-primary transition-colors shrink-0"
                                    title={`Giocatori ${awayTeam.name}`}
                                    data-testid={`players-link-${awayTeam.id}`}
                                  >
                                    <Users className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <span className="font-heading font-bold text-sm truncate">
                                  {awayTeam?.name || 'TBD'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Status & Action */}
                          <div className="flex items-center gap-2 justify-end">
                            {match.is_played && (
                              <span className="text-xs text-green-400 flex items-center gap-1">
                                <Check className="w-3 h-3" />
                              </span>
                            )}
                            <Button
                              variant={match.is_played ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleEditMatch(match)}
                              className={cn(
                                "text-xs",
                                match.is_played
                                  ? "border-slate-700 h-8"
                                  : "bg-primary text-slate-950 h-8"
                              )}
                              data-testid={`edit-match-${match.id}`}
                            >
                              {match.is_played ? 'Modifica' : 'Risultato'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-12 text-center">
              <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">
                {totalGroupMatches === 0
                  ? 'Nessuna partita in calendario. Genera prima il calendario dal sorteggio.'
                  : 'Nessuna partita corrisponde ai filtri'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingMatch} onOpenChange={() => setEditingMatch(null)}>
          <DialogContent className="bg-slate-900 border-slate-800">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingMatch?.is_played ? 'Modifica Risultato' : 'Inserisci Risultato'}
              </DialogTitle>
            </DialogHeader>

            {editingMatch && (
              <div className="py-6">
                <div className="flex items-center justify-center gap-6">
                  <div className="text-center flex-1">
                    <p className="font-heading font-bold mb-3 truncate">
                      {getTeamById(editingMatch.home_team_id)?.name}
                    </p>
                    <Input
                      type="number"
                      min="0"
                      value={scores.home}
                      onChange={(e) => setScores({ ...scores, home: e.target.value })}
                      className="text-center text-3xl font-numbers h-16 bg-slate-950 border-slate-800"
                      data-testid="home-score-input"
                    />
                  </div>

                  <span className="text-2xl text-slate-600 mt-8">-</span>

                  <div className="text-center flex-1">
                    <p className="font-heading font-bold mb-3 truncate">
                      {getTeamById(editingMatch.away_team_id)?.name}
                    </p>
                    <Input
                      type="number"
                      min="0"
                      value={scores.away}
                      onChange={(e) => setScores({ ...scores, away: e.target.value })}
                      className="text-center text-3xl font-numbers h-16 bg-slate-950 border-slate-800"
                      data-testid="away-score-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              {editingMatch?.is_played && (
                <Button
                  variant="outline"
                  onClick={handleResetResult}
                  className="border-red-700/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 sm:mr-auto"
                  data-testid="reset-result-btn"
                >
                  Annulla Risultato
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setEditingMatch(null)}
                className="border-slate-700"
              >
                Annulla
              </Button>
              <Button
                onClick={handleSaveResult}
                disabled={saving}
                className="bg-primary text-slate-950"
                data-testid="save-result-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPartitePage;
